import { ActionIcon, Avatar, Box, Group, Skeleton, Stack, Text, Tooltip } from '@mantine/core'
import { IconCamera, IconX, IconUpload } from '@tabler/icons-react'
import { FileButton } from 'components/FileButton'
import { Button } from 'components/Button'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AvatarUploadProps } from '../types'
import { CameraCapture } from './CameraCapture'
import { ImageCropModal } from './ImageCropModal'

const AVATAR_SIZE = 120
const ICON_SIZE = 16
const REMOVE_ICON_SIZE = 14
const ACCEPTED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const MIN_AVATAR_DIMENSION = 2

function buildInitials(firstName: string, lastName: string): string {
  const first = firstName.charAt(0).toUpperCase()
  const last = lastName.charAt(0).toUpperCase()
  return [first, last].filter(Boolean).join('') || '?'
}

/**
 * Circular avatar display with file upload, camera capture, and square crop.
 * All images go through a 1:1 crop modal before upload.
 */
export function AvatarUpload({
  avatarUrl,
  firstName,
  lastName,
  uploading,
  onFileSelect,
  onBase64Capture,
  onRemoveAvatar,
}: AvatarUploadProps) {
  const { t } = useTranslation()
  const [cameraOpen, setCameraOpen] = useState(false)
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  const [isPlaceholder, setIsPlaceholder] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(!avatarUrl)

  useEffect(() => {
    setIsPlaceholder(false)
    setImageLoaded(!avatarUrl)
  }, [avatarUrl])

  const hasAvatar = Boolean(avatarUrl) && !isPlaceholder
  const showSkeleton = (hasAvatar && !imageLoaded) || uploading

  function handleFileSelected(file: File) {
    setCropSrc(URL.createObjectURL(file))
  }

  function handleCameraCapture(base64: string) {
    setCameraOpen(false)
    setCropSrc(base64)
  }

  function handleCropDone(croppedBase64: string) {
    setCropSrc(null)
    onBase64Capture(croppedBase64)
  }

  return (
    <Stack align='center' spacing='xs'>
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        {showSkeleton && (
          <Skeleton height={AVATAR_SIZE} circle sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }} />
        )}
        <Avatar
          src={hasAvatar ? avatarUrl : null}
          size={AVATAR_SIZE}
          radius={AVATAR_SIZE}
          color='turquoise'
          imageProps={{
            onLoad: (e: React.SyntheticEvent<HTMLImageElement>) => {
              const img = e.currentTarget
              if (img.naturalWidth < MIN_AVATAR_DIMENSION || img.naturalHeight < MIN_AVATAR_DIMENSION) {
                setIsPlaceholder(true)
              } else {
                setImageLoaded(true)
              }
            },
          }}
        >
          {!hasAvatar && buildInitials(firstName, lastName)}
        </Avatar>

        {hasAvatar && !showSkeleton && (
          <Tooltip label={t('my_profile.remove_avatar')} position='right' withArrow>
            <ActionIcon
              color='red'
              variant='filled'
              size='sm'
              radius='xl'
              onClick={onRemoveAvatar}
              sx={{ position: 'absolute', top: 0, right: 0 }}
            >
              <IconX size={REMOVE_ICON_SIZE} />
            </ActionIcon>
          </Tooltip>
        )}
      </Box>

      <Group spacing='xs'>
        <FileButton accept={ACCEPTED_MIME_TYPES.join(',')} onChange={file => file && handleFileSelected(file)}>
          {props => (
            <Button {...props} variant='light' size='sm' loading={uploading} leftIcon={<IconUpload size={ICON_SIZE} />}>
              {t('my_profile.upload_image')}
            </Button>
          )}
        </FileButton>

        <Button
          variant='default'
          size='sm'
          leftIcon={<IconCamera size={ICON_SIZE} />}
          onClick={() => setCameraOpen(true)}
        >
          {t('my_profile.take_picture')}
        </Button>
      </Group>

      <Text size='xs' color='dimmed'>
        {t('my_profile.upload_hint')}
      </Text>

      <CameraCapture
        opened={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
      />

      <ImageCropModal
        opened={Boolean(cropSrc)}
        imageSrc={cropSrc || ''}
        onClose={() => setCropSrc(null)}
        onCrop={handleCropDone}
      />
    </Stack>
  )
}
